import React, { useState, useRef, useEffect, useMemo } from 'react';
import { getRecommendedPostcardShape } from '../utils/helpers';
import { PostType, Post, Attachment, Achievement, UserData, Comment } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import { useScrollLock } from '../hooks/useScrollLock';
import { compressVideo, compressAudio, uploadMediaFile } from '../utils/mediaUtils';
import { getLinkPreview } from '../utils/linkPreview';
import { updateUserOnServer } from '../api';
import api, { API_URL } from '../api';
import { AudioRecorder, AudioRecorderHandle, CustomAudioPlayer } from './AudioComponents';
import { useMentionSearch } from '../hooks/useMentionSearch';
import { stickerTextStyle } from './StickerText';
import { MentionDropdown } from './MentionDropdown';
import {
    ImageIcon,
    VideoIcon,
    AudioIcon,
    MicIcon,
    CloseIcon,
    GlobeIcon,
    PrivateIcon,
    ExternalLinkIcon,
    GalleryIcon,
    EyeIcon,
    EyeOffIcon,
    FireIcon,
    PostcardIcon,
    SendIcon,
    SettingsIcon,
    CopyIcon,
    PasteIcon,
    CameraIcon,
    SearchIcon,
    SparklesIcon,
    PlusIcon
} from './Icons';
import { NativeCamera } from './NativeCamera';
import { PostOptionsModal } from './Modals';
import { STORE_ITEMS } from '../constants';
import { Capacitor } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { requestMicrophonePermission } from '../utils/permissionUtils';

const urlRegex = /(https?:\/\/[^\s]+|www\.[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}[^\s]*|[a-zA-Z0-9.-]+\.(?:com|net|org|edu|gov|io|me|app|co|ai|xyz|ca|uk|fr|de|jp|it|br|ru|es|in|cn|tv)(?:\/[^\s]*)?)/gi;

interface PostCreatorProps {
    onCreatePost: (postData: {
        type: PostType,
        content: Post['content'],
        visibility?: 'public' | 'followers' | 'groups' | 'private',
        visibilityGroups?: string[],
        showCaption?: boolean,
        showOnProfile?: boolean,
        textSize?: 'xs' | 'sm' | 'med' | 'lg',
        communityId?: string,
        themeOpacity?: number,
        themeColor?: string,
        textColor?: string,
        isOptimistic?: boolean,
        localId?: number,
        fontId?: string,
        postcardStyle?: string,
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall',
        isForSale?: boolean,
        price?: number,
        vendorId?: string
    }) => void;
    postBlurOpacity: number;
    isFootprintPost?: boolean;
    achievement?: Achievement | null;
    isTaskOfTheDay?: boolean;
    onVouch?: (postId: number) => void;
    onCancel?: () => void;
    themeColor?: string;
    isCommunityPost?: boolean;
    onInternalModalStateChange?: (isOpen: boolean) => void;
    closeInternalModalsTrigger?: number;
    initialSharedContent?: { text: string, media: Attachment[], sourcePostId?: number } | null;
    clearSharedContent?: () => void;
    autoFocus?: boolean;
    onUploadFailed?: (localId: number) => void;
    footprintRequirements?: string[];
}

export const PostCreator = ({
    onCreatePost,
    postBlurOpacity,
    isFootprintPost = false,
    achievement,
    isTaskOfTheDay = false,
    onCancel,
    themeColor,
    isCommunityPost = false,
    onInternalModalStateChange,
    closeInternalModalsTrigger = 0,
    initialSharedContent,
    clearSharedContent,
    autoFocus = false,
    onUploadFailed,
    footprintRequirements = []
}: PostCreatorProps) => {

    const { currentUser, usersData, updateUserData } = useAuth();
    const isAdmin = usersData[currentUser]?.role === 'admin';
    const prevUserRef = useRef(currentUser);
    const [text, setText] = useState('');
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [coverImage, setCoverImage] = useState<{ url: string, file?: File } | null>(null);
    const [showRecorder, setShowRecorder] = useState(false);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
    const [linkPreview, setLinkPreview] = useState<Post['content']['linkPreview'] | null>(null);
    const [isFetchingPreview, setIsFetchingPreview] = useState(false);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    // Load persisted settings or use defaults
    const getPersisted = (key: string, defaultValue: any) => {
        const saved = localStorage.getItem(`post_creator_${key}_${currentUser}`);
        if (saved === null) return defaultValue;
        try { return JSON.parse(saved); } catch { return defaultValue; }
    };

    const [visibility, setVisibility] = useState<'public' | 'followers' | 'groups'>(() => {
        const persisted = getPersisted('visibility', (usersData[currentUser || '']?.defaultPostVisibility as any) || 'public');
        return persisted === 'private' ? 'followers' : (persisted || 'public');
    });
    const [visibilityGroups, setVisibilityGroups] = useState<string[]>(getPersisted('visibilityGroups', []));
    const [showCaption, setShowCaption] = useState<boolean>(getPersisted('showCaption', true));
    const [showOnProfile, setShowOnProfile] = useState<boolean>(getPersisted('showOnProfile', true));

    const [selectedFontId, setSelectedFontId] = useState<string>(() => getPersisted('fontId', 'default'));
    const [selectedPostcardStyle, setSelectedPostcardStyle] = useState<string>(() => getPersisted('postcardStyle', usersData[currentUser || '']?.postcardDefault || 'classic_postcards'));
    const [selectedThemeColor, setSelectedThemeColor] = useState<string>(() => getPersisted('themeColor', usersData[currentUser || '']?.appThemeColor || '#000000'));
    const [selectedTextColor, setSelectedTextColor] = useState<string>(() => getPersisted('textColor', usersData[currentUser || '']?.appTextColor || '#ffffff'));
    const [selectedThemeOpacity, setSelectedThemeOpacity] = useState<number>(() => getPersisted('themeOpacity', postBlurOpacity));
    const [textSize, setTextSize] = useState<'xs' | 'sm' | 'med' | 'lg'>(() => getPersisted('textSize', 'med'));
    const [postcardShape, setPostcardShape] = useState<'square' | 'wide' | 'tall' | 'wide_tall'>(() => getPersisted('postcardShape', 'wide'));
    const [showCustomizationModal, setShowCustomizationModal] = useState(false);
    
    // E-commerce state
    const [isForSale, setIsForSale] = useState(false);
    const [price, setPrice] = useState<string>('');
    
    const linkPreviewDebounceTimeout = useRef<number | null>(null);

    const mediaInputRef = useRef<HTMLInputElement>(null);
    const audioInputRef = useRef<HTMLInputElement>(null);
    const imageInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const coverImageInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraMode, setCameraMode] = useState<'image' | 'video' | 'both'>('both');
    const audioRecorderRef = useRef<AudioRecorderHandle>(null);

    const [showTenorModal, setShowTenorModal] = useState(false);
    
    // Audio Option Menu and Link Resolution States
    const [showAudioMenuModal, setShowAudioMenuModal] = useState(false);
    const [activeInputService, setActiveInputService] = useState<'spotify' | 'apple' | 'youtube' | null>(null);
    const [externalLink, setExternalLink] = useState('');
    const [externalTitle, setExternalTitle] = useState('');
    const [externalError, setExternalError] = useState('');
    const [resolvedExternalData, setResolvedExternalData] = useState<{
        previewUrl?: string;
        coverArt?: string;
        title?: string;
    } | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [tenorSearchMode, setTenorSearchMode] = useState<'attachment' | 'cover'>('attachment');
    const [tenorSearch, setTenorSearch] = useState('');
    const [tenorResults, setTenorResults] = useState<any[]>([]);
    const [loadingTenor, setLoadingTenor] = useState(false);

    const searchTenor = async (query: string) => {
        try {
            const response = await api.get(`tenor/search?q=${encodeURIComponent(query)}`);
            const data = response.data;
            return data.results.map((r: any) => ({
                id: r.id,
                url: r.media[0].gif.url,
                name: r.title || 'Tenor GIF'
            }));
        } catch (e) {
            console.error(e);
            return [];
        }
    };

    const handleTenorSearch = async (query: string) => {
        setTenorSearch(query);
        if (!query.trim()) {
            setTenorResults([]);
            return;
        }
        setLoadingTenor(true);
        try {
            const results = await searchTenor(query);
            setTenorResults(results);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingTenor(false);
        }
    };

    useEffect(() => {
        if (!autoFocus) return;
        // Attempt to focus after a short delay to allow for animations/rendering
        const timer = setTimeout(() => {
            textareaRef.current?.focus();
        }, 500);
        return () => clearTimeout(timer);
    }, [autoFocus]);

    // Sync camera state with App's internal modal tracker to suppress swipe/scroll
    useEffect(() => {
        if (onInternalModalStateChange) {
            onInternalModalStateChange(showCamera);
        }
    }, [showCamera, onInternalModalStateChange]);
    const { mentionQuery, updateSearch, completeMention } = useMentionSearch();
    // Calculate total attachment size (in bytes)
    const totalAttachmentSize = attachments.reduce((sum, att) => sum + (att.file?.size || 0), 0);
    const MAX_POST_SIZE = 500 * 1024 * 1024; // 500MB


    useEffect(() => {
        if (isFootprintPost || attachments.length > 0 || linkPreview) {
            return;
        }

        if (linkPreviewDebounceTimeout.current) {
            clearTimeout(linkPreviewDebounceTimeout.current);
        }

        linkPreviewDebounceTimeout.current = window.setTimeout(() => {
            if (isFetchingPreview) return;

            const urls = text.match(urlRegex);
            if (urls && urls.length > 0) {
                setIsFetchingPreview(true);
                getLinkPreview(urls[0])
                    .then(preview => {
                        if (preview) {
                            setLinkPreview(preview);
                            if (preview.url?.includes('/shorts/') || preview.isYouTubeShort) {
                                setPostcardShape('tall');
                            } else if (
                                preview.url?.includes('spotify.com') ||
                                preview.url?.includes('apple.com') ||
                                preview.url?.includes('music.apple.com')
                            ) {
                                setPostcardShape('wide');
                            }
                            setText(currentText => {
                                // Only clear the URL if it's the only thing in the box (or just whitespace around it)
                                if (currentText.trim() === urls[0]) {
                                    return '';
                                }
                                // Otherwise keep the URL in the text so it can be hyperlinked
                                return currentText;
                            });
                        }
                    })
                    .catch(err => {
                        console.error("[PostCreator] Metadata fetch error:", err);
                    })
                    .finally(() => {
                        setIsFetchingPreview(false);
                    });
            }
        }, 500);

        return () => {
            if (linkPreviewDebounceTimeout.current) {
                clearTimeout(linkPreviewDebounceTimeout.current);
            }
        }
    }, [text, attachments.length, linkPreview, isFootprintPost]);

    // Handle initial shared content
    useEffect(() => {
        if (initialSharedContent) {
            if (initialSharedContent.text) setText(initialSharedContent.text);
            if (initialSharedContent.media && initialSharedContent.media.length > 0) {
                const newAttachments: Attachment[] = initialSharedContent.media.map(m => ({
                    url: m.url,
                    type: m.type as any,
                    width: m.width,
                    height: m.height
                }));
                setAttachments(prev => [...prev, ...newAttachments]);

                // Default shape based on shared media
                const first = newAttachments[0];
                setPostcardShape(getRecommendedPostcardShape(first.type, first));
            }
            if (clearSharedContent) clearSharedContent();
        }
    }, [initialSharedContent, clearSharedContent]);

    const resetState = () => {
        setText('');
        setAttachments([]);
        setCoverImage(null);
        setShowRecorder(false);
        setIsForSale(false);
        setPrice('');
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
            setAudioStream(null);
        }
        setLinkPreview(null);
        setVisibilityGroups(getPersisted('visibilityGroups', []));
        setSelectedFontId(getPersisted('fontId', 'default'));
        setSelectedPostcardStyle(getPersisted('postcardStyle', usersData[currentUser || '']?.postcardDefault || 'classic_postcards'));
        setSelectedThemeColor(getPersisted('themeColor', usersData[currentUser || '']?.appThemeColor || '#000000'));
        setSelectedTextColor(getPersisted('textColor', usersData[currentUser || '']?.appTextColor || '#ffffff'));
        setSelectedThemeOpacity(getPersisted('themeOpacity', postBlurOpacity));
        setTextSize(getPersisted('textSize', 'med'));
        setPostcardShape(getPersisted('postcardShape', 'wide'));
    };

    // Persist settings
    useEffect(() => {
        if (!currentUser) return;
        if (prevUserRef.current !== currentUser) return;
        localStorage.setItem(`post_creator_visibility_${currentUser}`, JSON.stringify(visibility));
        localStorage.setItem(`post_creator_showCaption_${currentUser}`, JSON.stringify(showCaption));
        localStorage.setItem(`post_creator_showOnProfile_${currentUser}`, JSON.stringify(showOnProfile));
    }, [visibility, showCaption, showOnProfile, currentUser]);

    // Persist customization settings
    useEffect(() => {
        if (!currentUser) return;
        if (prevUserRef.current !== currentUser) return;
        localStorage.setItem(`post_creator_fontId_${currentUser}`, JSON.stringify(selectedFontId));
        localStorage.setItem(`post_creator_postcardStyle_${currentUser}`, JSON.stringify(selectedPostcardStyle));
        localStorage.setItem(`post_creator_themeColor_${currentUser}`, JSON.stringify(selectedThemeColor));
        localStorage.setItem(`post_creator_textColor_${currentUser}`, JSON.stringify(selectedTextColor));
        localStorage.setItem(`post_creator_themeOpacity_${currentUser}`, JSON.stringify(selectedThemeOpacity));
        localStorage.setItem(`post_creator_textSize_${currentUser}`, JSON.stringify(textSize));
        localStorage.setItem(`post_creator_postcardShape_${currentUser}`, JSON.stringify(postcardShape));
    }, [selectedFontId, selectedPostcardStyle, selectedThemeColor, selectedTextColor, selectedThemeOpacity, textSize, postcardShape, currentUser]);

    // Update settings when user switches accounts
    useEffect(() => {
        if (!currentUser) return;

        const uData = usersData[currentUser];
        const defaultVisibility = (uData?.defaultPostVisibility as any) || 'public';

        setVisibility(getPersisted('visibility', defaultVisibility === 'private' ? 'followers' : defaultVisibility));
        setVisibilityGroups(getPersisted('visibilityGroups', []));
        setShowCaption(getPersisted('showCaption', true));
        setShowOnProfile(getPersisted('showOnProfile', true));

        setSelectedFontId(getPersisted('fontId', 'default'));
        setSelectedPostcardStyle(getPersisted('postcardStyle', uData?.postcardDefault || 'classic_postcards'));
        setSelectedThemeColor(getPersisted('themeColor', uData?.appThemeColor || '#000000'));
        setSelectedTextColor(getPersisted('textColor', uData?.appTextColor || '#ffffff'));
        setSelectedThemeOpacity(getPersisted('themeOpacity', postBlurOpacity));
        setTextSize(getPersisted('textSize', 'med'));
        setPostcardShape(getPersisted('postcardShape', 'wide'));

        prevUserRef.current = currentUser;
    }, [currentUser, usersData, postBlurOpacity]);

    useScrollLock(showCustomizationModal);
    useScrollLock(showTenorModal);
    useScrollLock(showAudioMenuModal);

    useEffect(() => {
        onInternalModalStateChange?.(showCustomizationModal || showTenorModal || showAudioMenuModal);
    }, [showCustomizationModal, showTenorModal, showAudioMenuModal, onInternalModalStateChange]);

    useEffect(() => {
        if (closeInternalModalsTrigger > 0) {
            setShowCustomizationModal(false);
            setShowTenorModal(false);
            setShowAudioMenuModal(false);
        }
    }, [closeInternalModalsTrigger]);

    const fetchSpotifyOembed = async (url: string) => {
        try {
            const oembedUrl = `https://open.spotify.com/oembed?url=${encodeURIComponent(url)}`;
            const res = await fetch(oembedUrl);
            if (res.ok) {
                return await res.json();
            }
        } catch (err) {
            console.error('Failed to fetch Spotify oembed on client:', err);
        }
        return null;
    };

    // Auto-resolve metadata on paste/change of externalLink in PostCreator
    useEffect(() => {
        const link = externalLink.trim();
        if (!link) {
            setResolvedExternalData(null);
            return;
        }

        const isSpotify = link.includes('spotify.com');
        const isApple = link.includes('apple.com') || link.includes('music.apple.com');
        const isYoutube = link.includes('youtube.com') || link.includes('youtu.be');

        if ((activeInputService === 'spotify' && isSpotify) ||
            (activeInputService === 'apple' && isApple) ||
            (activeInputService === 'youtube' && isYoutube)) {
            
            const controller = new AbortController();
            const fetchPreview = async () => {
                setExternalError('');
                setExternalTitle('Fetching track info...');
                setResolvedExternalData(null);
                try {
                    let endpoint = '';
                    if (isSpotify) {
                        const oembed = await fetchSpotifyOembed(link);
                        // oEmbed title is formatted as "Track Name by Artist Name" — split to send precise params
                        const oembedTitle = oembed?.title || '';
                        const byIndex = oembedTitle.lastIndexOf(' by ');
                        const trackName = byIndex > 0 ? oembedTitle.substring(0, byIndex).trim() : oembedTitle.trim();
                        const artistName = byIndex > 0 ? oembedTitle.substring(byIndex + 4).trim() : '';
                        const titleParam = trackName ? `&title=${encodeURIComponent(trackName)}` : '';
                        const artistParam = artistName ? `&artist=${encodeURIComponent(artistName)}` : '';
                        const coverParam = oembed?.thumbnail_url ? `&coverImg=${encodeURIComponent(oembed.thumbnail_url)}` : '';
                        endpoint = `${API_URL}spotify-preview?url=${encodeURIComponent(link)}${titleParam}${artistParam}${coverParam}`;
                    } else if (isApple) {
                        endpoint = `${API_URL}apple-music-preview?url=${encodeURIComponent(link)}`;
                    } else if (isYoutube) {
                        endpoint = `${API_URL}youtube-music-preview?url=${encodeURIComponent(link)}`;
                    }

                    const response = await fetch(endpoint, { signal: controller.signal });
                    if (response.ok) {
                        const data = await response.json();
                        let fetchedTitle = '';
                        if (data.title && data.artist) {
                            fetchedTitle = `${data.title} - ${data.artist}`;
                        } else if (data.title) {
                            fetchedTitle = data.title;
                        }
                        if (fetchedTitle) {
                            setExternalTitle(fetchedTitle);
                        } else {
                            setExternalTitle('');
                        }
                        setResolvedExternalData({
                            previewUrl: data.previewUrl,
                            coverArt: data.coverImg,
                            title: fetchedTitle
                        });
                    } else {
                        setExternalTitle('');
                        setResolvedExternalData(null);
                    }
                } catch (err: any) {
                    if (err.name !== 'AbortError') {
                        console.error('Failed to pre-fetch details:', err);
                        setExternalTitle('');
                        setResolvedExternalData(null);
                    }
                }
            };

            const delayDebounce = setTimeout(() => {
                fetchPreview();
            }, 500);

            return () => {
                clearTimeout(delayDebounce);
                controller.abort();
            };
        }
    }, [externalLink, activeInputService]);

    const handleAddStreamingAudio = async (e: React.FormEvent) => {
        e.preventDefault();
        setExternalError('');

        if (!externalLink.trim()) {
            setExternalError('Please enter a link.');
            return;
        }

        const isSpotify = externalLink.includes('spotify.com');
        const isApple = externalLink.includes('apple.com') || externalLink.includes('music.apple.com');
        const isYoutube = externalLink.includes('youtube.com') || externalLink.includes('youtu.be');

        if (activeInputService === 'spotify' && !isSpotify) {
            setExternalError('Please enter a valid Spotify link.');
            return;
        }
        if (activeInputService === 'apple' && !isApple) {
            setExternalError('Please enter a valid Apple Music link.');
            return;
        }
        if (activeInputService === 'youtube' && !isYoutube) {
            setExternalError('Please enter a valid YouTube/YouTube Music link.');
            return;
        }

        // Check file limits and type conflicts
        const existingIsMedia = attachments.length > 0 && (attachments[0].type === 'image' || attachments[0].type === 'video');
        if (existingIsMedia) {
            alert("You can't mix photos/videos with audio files in the same post.");
            return;
        }
        if (attachments.length + 1 > 5) {
            alert("You can only upload up to 5 audio files.");
            return;
        }

        let finalUrl = externalLink.trim();
        let originalUrl = externalLink.trim();
        let coverArt = '';
        let fetchedTitle = '';

        // Always use cached metadata if available (title, cover) even if previewUrl was null
        if (resolvedExternalData) {
            coverArt = resolvedExternalData.coverArt || '';
            fetchedTitle = resolvedExternalData.title || '';
        }

        // Only skip re-fetch if we already have a valid playable preview URL
        if (resolvedExternalData?.previewUrl) {
            finalUrl = resolvedExternalData.previewUrl;
        } else {
            setIsUploading(true);
            if (isSpotify) {
                try {
                    const oembed = await fetchSpotifyOembed(originalUrl);
                    const oembedTitleC = oembed?.title || '';
                    const byIdxC = oembedTitleC.lastIndexOf(' by ');
                    const trackNameC = byIdxC > 0 ? oembedTitleC.substring(0, byIdxC).trim() : oembedTitleC.trim();
                    const artistNameC = byIdxC > 0 ? oembedTitleC.substring(byIdxC + 4).trim() : '';
                    const titleParamC = trackNameC ? `&title=${encodeURIComponent(trackNameC)}` : '';
                    const artistParamC = artistNameC ? `&artist=${encodeURIComponent(artistNameC)}` : '';
                    const coverParamC = oembed?.thumbnail_url ? `&coverImg=${encodeURIComponent(oembed.thumbnail_url)}` : '';
                    const response = await fetch(`${API_URL}spotify-preview?url=${encodeURIComponent(originalUrl)}${titleParamC}${artistParamC}${coverParamC}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.previewUrl) finalUrl = data.previewUrl;
                        if (data.coverImg) coverArt = data.coverImg;
                        if (data.title && data.artist) fetchedTitle = `${data.title} - ${data.artist}`;
                        else if (data.title) fetchedTitle = data.title;
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch Spotify preview:', err);
                }
            } else if (isApple) {
                try {
                    const response = await fetch(`${API_URL}apple-music-preview?url=${encodeURIComponent(originalUrl)}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.previewUrl) finalUrl = data.previewUrl;
                        if (data.coverImg) coverArt = data.coverImg;
                        if (data.title && data.artist) fetchedTitle = `${data.title} - ${data.artist}`;
                        else if (data.title) fetchedTitle = data.title;
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch Apple Music preview:', err);
                }
            } else if (isYoutube) {
                try {
                    const response = await fetch(`${API_URL}youtube-music-preview?url=${encodeURIComponent(originalUrl)}`);
                    if (response.ok) {
                        const data = await response.json();
                        if (data.previewUrl) finalUrl = data.previewUrl;
                        if (data.coverImg) coverArt = data.coverImg;
                        if (data.title && data.artist) fetchedTitle = `${data.title} - ${data.artist}`;
                        else if (data.title) fetchedTitle = data.title;
                    }
                } catch (err) {
                    console.error('Failed to pre-fetch YouTube preview:', err);
                }
            }
            setIsUploading(false);
        }

        const defaultTitle = fetchedTitle || (isSpotify ? 'Spotify Track' : isApple ? 'Apple Music Track' : 'YouTube Track');
        const finalTitle = externalTitle.trim() || defaultTitle;

        // Add as an audio attachment behaving exactly like direct uploads!
        const pseudoFile = new File([], finalTitle, { type: 'audio/mpeg' });
        const newAttachment: Attachment = {
            url: finalUrl,
            type: 'audio',
            file: pseudoFile,
            originalUrl: originalUrl,
            name: finalTitle
        };

        setAttachments(prev => [...prev, newAttachment]);
        setPostcardShape(getRecommendedPostcardShape('audio', newAttachment));

        // If we resolved a cover image art, pre-populate that too!
        if (coverArt && !coverImage) {
            setCoverImage({ url: coverArt });
        }

        // Reset inputs and close menu
        setExternalLink('');
        setExternalTitle('');
        setExternalError('');
        setActiveInputService(null);
        setShowAudioMenuModal(false);
    };

    const handleFileUploadForPost = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Check file limits and type conflicts
        const existingIsMedia = attachments.length > 0 && (attachments[0].type === 'image' || attachments[0].type === 'video');
        if (existingIsMedia) {
            alert("You can't mix photos/videos with audio files in the same post.");
            return;
        }
        if (attachments.length + 1 > 5) {
            alert("You can only upload up to 5 audio files.");
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const url = await uploadMediaFile(file, currentUser, (progress) => {
                setUploadProgress(Math.round(progress));
            });

            const newAttachment: Attachment = {
                url: url,
                type: 'audio',
                file: file
            };

            setAttachments(prev => [...prev, newAttachment]);
            setPostcardShape(getRecommendedPostcardShape('audio', newAttachment));
            setShowAudioMenuModal(false);
        } catch (err) {
            console.error('Failed to upload audio file', err);
            alert('Failed to upload audio file.');
        } finally {
            setIsUploading(false);
            if (audioInputRef.current) audioInputRef.current.value = '';
        }
    };

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const handleAttachTenorGif = (gifUrl: string) => {
        if (tenorSearchMode === 'cover') {
            setCoverImage({ url: gifUrl });
            setShowTenorModal(false);
            return;
        }

        const existingIsAudio = attachments.length > 0 && (attachments[0].type === 'audio' || attachments[0].type === 'voice');
        if (existingIsAudio) {
            alert("You can't mix photos/videos with audio files in the same post.");
            return;
        }

        if (attachments.length + 1 > 100) {
            alert("You can only upload up to 100 photos/videos.");
            return;
        }

        setAttachments(prev => [...prev, { url: gifUrl, type: 'image' }]);
        setShowTenorModal(false);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, explicitType?: 'image' | 'video' | 'audio', isLive: boolean = false) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;

        setShowRecorder(false);
        setLinkPreview(null);

        // Determine incoming file types
        const incomingHasAudio = files.some(f => f.type.startsWith('audio/'));
        const incomingHasMedia = files.some(f => f.type.startsWith('image/') || f.type.startsWith('video/'));

        const existingIsMedia = attachments.length > 0 && (attachments[0].type === 'image' || attachments[0].type === 'video');
        const existingIsAudio = attachments.length > 0 && (attachments[0].type === 'audio' || attachments[0].type === 'voice');

        if ((existingIsMedia && incomingHasAudio) || (existingIsAudio && incomingHasMedia)) {
            alert("You can't mix photos/videos with audio files in the same post.");
            return;
        }

        const limits = { media: 100, audio: 5 };
        const currentCount = existingIsMedia ? attachments.length : attachments.filter(a => a.type === 'audio' || a.type === 'voice').length;
        const limit = (incomingHasAudio && !incomingHasMedia) ? limits.audio : limits.media;

        if (currentCount + files.length > limit) {
            alert(`You can only upload up to ${limit} ${limit === limits.media ? 'photos/videos' : 'audio files'}.`);
            return;
        }

        if (incomingHasAudio && !incomingHasMedia) {
            const fileNames = files.map(f => f.name).join(', ');
            setText(prev => prev ? `${prev}\n${fileNames}` : fileNames);
        }

        // Check if adding these files would exceed the 500MB limit
        const newTotalSize = files.reduce((sum, file) => sum + file.size, totalAttachmentSize);
        if (newTotalSize > MAX_POST_SIZE) {
            alert('Total attachments size exceeds 500MB. Please remove some files.');
            return;
        }

        setIsUploadingMedia(true);
        const processFiles = async () => {
            const processedFiles = await Promise.all(
                files.map(async (file) => {
                    let processedFile = file;
                    const detectedType = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'audio';
                    if (detectedType === 'video') {
                        try {
                            const blob = await compressVideo(file);
                            processedFile = new File([blob], file.name, { type: file.type });
                        } catch (e) {
                            console.error("Video compression failed", e);
                        }
                    } else if (detectedType === 'audio') {
                        try {
                            const blob = await compressAudio(file);
                            processedFile = new File([blob], file.name, { type: file.type });
                        } catch (e) {
                            console.error("Audio compression failed", e);
                        }
                    }

                    const url = URL.createObjectURL(processedFile);
                    const attachmentBase = { url, file: processedFile, type: detectedType, isLive } as Attachment;

                    if (detectedType === 'image') {
                        return new Promise<Attachment>((resolve) => {
                            const img = new Image();
                            img.onload = () => {
                                resolve({ ...attachmentBase, width: img.naturalWidth, height: img.naturalHeight } as Attachment);
                            };
                            img.onerror = () => resolve(attachmentBase as Attachment);
                            img.src = url;
                        });
                    }
                    if (detectedType === 'video') {
                        return new Promise<Attachment>((resolve) => {
                            const videoEl = document.createElement('video');
                            videoEl.preload = 'metadata';
                            const timeoutId = setTimeout(() => resolve(attachmentBase as Attachment), 5000);
                            videoEl.onloadedmetadata = () => {
                                clearTimeout(timeoutId);
                                resolve({ ...attachmentBase, width: videoEl.videoWidth, height: videoEl.videoHeight } as Attachment);
                            };
                            videoEl.onerror = () => {
                                clearTimeout(timeoutId);
                                resolve(attachmentBase as Attachment);
                            };
                            videoEl.src = url;
                            videoEl.load();
                        });
                    }
                    return attachmentBase as Attachment;
                })
            );
            setAttachments(prev => [...prev, ...processedFiles]);
            setIsUploadingMedia(false);

            // Set defaults based on first attachment
            const first = processedFiles[0];
            setPostcardShape(getRecommendedPostcardShape(first.type, first));
        };
        processFiles();
    };

    const handleCoverImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setCoverImage({ url: URL.createObjectURL(file), file });
        }
    }

    const removeAttachment = (urlToRemove: string) => {
        setAttachments(prev => {
            const next = prev.filter(a => a.url !== urlToRemove);
            if (next.length === 0) {
                setPostcardShape('wide');
            } else if (prev.length > 0 && next.length > 0 && prev[0].url === urlToRemove) {
                const first = next[0];
                setPostcardShape(getRecommendedPostcardShape(first.type, first));
            }
            return next;
        });
    };

    const { startUpload } = useUpload();

    const handlePost = () => {
        if (isFootprintPost && attachments.length === 0) return;
        if (!isFootprintPost && !text.trim() && attachments.length === 0 && !linkPreview) return;

        // Validate Footprint attachment type requirements
        if (isFootprintPost && footprintRequirements.length > 0) {
            const hasVoice = attachments.some(a => a.type === 'voice');
            const hasAudio = attachments.some(a => a.type === 'audio');
            const hasLiveCamera = attachments.some(a => a.isLive && (a.type === 'image' || a.type === 'video'));

            const missingReqs: string[] = [];
            if (footprintRequirements.includes('voice') && !hasVoice) missingReqs.push('Voice recording');
            if (footprintRequirements.includes('audio') && !hasAudio) missingReqs.push('Audio file');
            if (footprintRequirements.includes('live_camera') && !hasLiveCamera) missingReqs.push('Live Camera photo/video');

            if (missingReqs.length > 0) {
                alert(`This footprint requires the following attachment types:\n• ${missingReqs.join('\n• ')}`);
                return;
            }
        }

        let postType: PostType = 'text';
        if (linkPreview) postType = 'link';
        else if (attachments.length > 0) postType = attachments[0].type;

        const finalVisibility = isFootprintPost ? 'public' : visibility;

        const basePostData = {
            type: postType,
            content: {
                text: text.trim(),
                media: attachments.map(a => ({ 
                    url: a.url, 
                    type: a.type, 
                    width: a.width, 
                    height: a.height, 
                    isLive: a.isLive,
                    originalUrl: a.originalUrl,
                    name: a.name || a.file?.name
                })),
                coverImageUrl: coverImage?.url,
                linkPreview: linkPreview || undefined
            },
            visibility: finalVisibility,
            visibilityGroups: finalVisibility === 'groups' ? visibilityGroups : [],
            showCaption,
            showOnProfile,
            themeOpacity: selectedThemeOpacity,
            themeColor: selectedThemeColor,
            textColor: selectedTextColor,
            fontId: selectedFontId,
            postcardStyle: selectedPostcardStyle,
            textSize,
            postcardShape,
            isForSale,
            price: isForSale ? (parseFloat(price) || 0) : undefined,
            vendorId: isForSale ? usersData[currentUser || '']?.vendorId : undefined
        };

        if (attachments.length > 0) {
            // Optimistic media post upload flow
            const postWithLocalId = {
                ...basePostData,
                isOptimistic: true,
                localId: Date.now()
            } as any;

            // Start the background upload with overrideLocalId passed in
            startUpload(
                currentUser || '',
                postWithLocalId,
                attachments,
                coverImage ? { url: coverImage.url, file: coverImage.file } : null,
                onCreatePost,
                onUploadFailed,
                postWithLocalId.localId
            );
        } else {
            // Text-only/link post flow. Saves immediately to DB.
            onCreatePost({
                ...basePostData,
                localId: Date.now()
            } as any);
        }

        resetState();
        if (onCancel) onCancel();
    };

    const handleCameraCapture = (file: File) => {
        // Create an event-like object to reuse handleFileChange
        const pseudoEvent = {
            target: {
                files: [file]
            }
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleFileChange(pseudoEvent, undefined, true);
    };

    const handleGalleryClick = async () => {
        mediaInputRef.current?.click();
    };

    const handleRecorderComplete = (url: string, blob: Blob, durationSeconds: number) => {
        const mimeType = blob.type || 'audio/webm';
        let extension = 'webm';
        if (mimeType.includes('mp4')) extension = 'mp4';
        else if (mimeType.includes('ogg')) extension = 'ogg';
        else if (mimeType.includes('wav')) extension = 'wav';
        
        const sizeKb = Math.round(blob.size / 1024);
        const file = new File([blob], `voice-${sizeKb}kb.${extension}`, { type: mimeType });
        setAttachments(prev => [...prev, { url, type: 'voice', file, duration: durationSeconds }]);
        setShowRecorder(false);
        
        if (attachments.length === 0 && !isFootprintPost) {
            setPostcardShape(getRecommendedPostcardShape('voice', {}));
        }
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
            setAudioStream(null);
        }
    };

    const handleRecorderReset = () => {
        setAttachments([]);
        setShowRecorder(false);
        if (audioStream) {
            audioStream.getTracks().forEach(track => track.stop());
            setAudioStream(null);
        }
    };

    const isAudioPost = attachments.length > 0 && attachments.every(a => a.type === 'audio' || a.type === 'voice');
    const isSubmitDisabled = isFootprintPost
        ? attachments.length === 0 || totalAttachmentSize > MAX_POST_SIZE
        : (!text.trim() && attachments.length === 0 && !linkPreview) || totalAttachmentSize > MAX_POST_SIZE;

    const fontOptions = [
        { id: 'default', name: 'Default', family: 'inherit' },
        { id: 'font_serif', name: 'Serif', family: 'Georgia, serif' },
        { id: 'font_mono', name: 'Mono', family: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' },
        { id: 'font_cursive', name: 'Cursive', family: '"Comic Sans MS", cursive, sans-serif' },
        { id: 'font_inter', name: 'Inter', family: '"Inter", sans-serif' },
        { id: 'font_playfair', name: 'Playfair', family: '"Playfair Display", serif' },
        { id: 'font_roboto_mono', name: 'Roboto Mono', family: '"Roboto Mono", monospace' },
        { id: 'font_pacifico', name: 'Pacifico', family: '"Pacifico", cursive' },
        { id: 'font_bangers', name: 'Bangers', family: '"Bangers", cursive' },
        { id: 'font_outfit', name: 'Outfit', family: '"Outfit", sans-serif' },
        { id: 'font_roboto', name: 'Roboto', family: '"Roboto", sans-serif' },
        { id: 'font_open_sans', name: 'Open Sans', family: '"Open Sans", sans-serif' },
        { id: 'font_lato', name: 'Lato', family: '"Lato", sans-serif' },
        { id: 'font_montserrat', name: 'Montserrat', family: '"Montserrat", sans-serif' },
        { id: 'font_oswald', name: 'Oswald', family: '"Oswald", sans-serif' },
        { id: 'font_raleway', name: 'Raleway', family: '"Raleway", sans-serif' },
        { id: 'font_merriweather', name: 'Merriweather', family: '"Merriweather", serif' },
        { id: 'font_dancing_script', name: 'Dancing', family: '"Dancing Script", cursive' },
        { id: 'font_lobster', name: 'Lobster', family: '"Lobster", cursive' },
        { id: 'font_caveat', name: 'Caveat', family: '"Caveat", cursive' },
        { id: 'font_permanent_marker', name: 'Marker', family: '"Permanent Marker", cursive' },
        { id: 'font_righteous', name: 'Righteous', family: '"Righteous", cursive' },
        { id: 'font_creepster', name: 'Creepster', family: '"Creepster", cursive' },
        { id: 'font_press_start_2p', name: '8-Bit', family: '"Press Start 2P", cursive' },
        { id: 'font_monoton', name: 'Monoton', family: '"Monoton", cursive' },
        { id: 'font_vt323', name: 'VT323', family: '"VT323", monospace' },
        { id: 'font_bebas_neue', name: 'Bebas Neue', family: '"Bebas Neue", sans-serif' },
        { id: 'font_anton', name: 'Anton', family: '"Anton", sans-serif' },
        { id: 'font_josefin_sans', name: 'Josefin', family: '"Josefin Sans", sans-serif' },
        { id: 'font_comfortaa', name: 'Comfortaa', family: '"Comfortaa", cursive' },
        { id: 'font_shadows_into_light', name: 'Shadows', family: '"Shadows Into Light", cursive' },
        { id: 'font_amatic_sc', name: 'Amatic', family: '"Amatic SC", cursive' },
        { id: 'font_indie_flower', name: 'Indie Flower', family: '"Indie Flower", cursive' },
        { id: 'font_kalam', name: 'Kalam', family: '"Kalam", cursive' },
        { id: 'font_satisfy', name: 'Satisfy', family: '"Satisfy", cursive' },
        { id: 'font_courgette', name: 'Courgette', family: '"Courgette", cursive' },
        { id: 'font_great_vibes', name: 'Great Vibes', family: '"Great Vibes", cursive' },
        { id: 'font_bungee', name: 'Bungee', family: '"Bungee", cursive' },
        { id: 'font_alfa_slab_one', name: 'Alfa Slab', family: '"Alfa Slab One", cursive' },
        { id: 'font_abril_fatface', name: 'Abril', family: '"Abril Fatface", cursive' }
    ];

    const selectedFont = fontOptions.find(f => f.id === selectedFontId);

    const postColorRgb = useMemo(() => {
        const hex = selectedThemeColor.replace('#', '');
        if (hex.length !== 6) return null;
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return isNaN(r) || isNaN(g) || isNaN(b) ? null : `${r}, ${g}, ${b}`;
    }, [selectedThemeColor]);

    const creatorBg = useMemo(() => {
        if (postColorRgb) {
            return `rgba(${postColorRgb}, ${selectedThemeOpacity / 100})`;
        }
        return 'var(--app-theme-bg)';
    }, [postColorRgb, selectedThemeOpacity]);


    const handlePaste = async (e: React.ClipboardEvent) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        const files: File[] = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                const file = items[i].getAsFile();
                if (file && (file.type.startsWith('image/') || file.type.startsWith('video/') || file.type.startsWith('audio/'))) {
                    files.push(file);
                }
            }
        }

        if (files.length > 0) {
            e.preventDefault();

            const newTotalSize = files.reduce((sum, file) => sum + file.size, totalAttachmentSize);
            if (newTotalSize > MAX_POST_SIZE) {
                alert('Total attachments size exceeds 500MB. Please remove some files before pasting.');
                return;
            }

            // Re-use logic from handleFileChange but for individual pasted files
            setIsUploadingMedia(true);
            try {
                const processedFiles = await Promise.all(
                    files.map(async (file) => {
                        let processedFile = file;
                        const detectedType = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'audio';

                        // Basic limit check (simplified for paste)
                        if (detectedType === 'video') {
                            const blob = await compressVideo(file);
                            processedFile = new File([blob], file.name, { type: file.type });
                        } else if (detectedType === 'audio') {
                            const blob = await compressAudio(file);
                            processedFile = new File([blob], file.name, { type: file.type });
                        }

                        const url = URL.createObjectURL(processedFile);
                        const attachmentBase = { url, file: processedFile, type: detectedType } as Attachment;

                        if (detectedType === 'video') {
                            return new Promise<Attachment>((resolve) => {
                                const videoEl = document.createElement('video');
                                videoEl.preload = 'metadata';
                                const timeoutId = setTimeout(() => resolve(attachmentBase as Attachment), 5000);
                                videoEl.onloadedmetadata = () => {
                                    clearTimeout(timeoutId);
                                    resolve({ ...attachmentBase, width: videoEl.videoWidth, height: videoEl.videoHeight } as Attachment);
                                };
                                videoEl.onerror = () => {
                                    clearTimeout(timeoutId);
                                    resolve(attachmentBase as Attachment);
                                };
                                videoEl.src = url;
                                videoEl.load();
                            });
                        }
                        return attachmentBase as Attachment;
                    })
                );
                setAttachments(prev => [...prev, ...processedFiles]);

                const first = processedFiles[0];
                setPostcardShape(getRecommendedPostcardShape(first.type, first));
            } catch (error) {
                console.error("Paste processing failed", error);
            } finally {
                setIsUploadingMedia(false);
            }
        }
    };

    return (
        <>
            <div
                className="w-full transition-all duration-300 post-creator-container"
            >
                <style>{`
                    .custom-post-creator-textarea::placeholder {
                        color: ${selectedTextColor};
                        opacity: 0.5;
                        text-shadow: ${stickerTextStyle(0.18).textShadow};
                        text-align: center;
                    }
                `}</style>
                <div
                    className="w-full md:max-w-[50%] md:mx-auto p-3 transition-all duration-300"
                    style={{ backgroundColor: 'transparent' }}
                >
                    {isUploadingMedia && (
                        <div className="text-center text-blue-400 p-4">Uploading media, please wait...</div>
                    )}
                    {isFootprintPost && achievement && (
                        <div className="mb-4 p-3 border border-gray-700 rounded-lg bg-gray-800/50">
                            <div className="flex justify-between items-start">
                                <p className="font-bold text-lg">Completed: {achievement.title}</p>
                                <span className="text-yellow-400 font-bold">{isTaskOfTheDay ? achievement.points : Math.round(achievement.points * 0.2)} pts</span>
                            </div>
                            <p className="text-sm text-gray-400">{achievement.description}</p>
                        </div>
                    )}
                    {isFootprintPost && (
                        <div className="mb-2 -mt-2 text-center text-xs">
                            <p className="text-yellow-400 font-semibold mb-1">
                                ⚠️ A media attachment is required.
                            </p>
                            {footprintRequirements.length > 0 && (
                                <p className="text-red-300 font-bold" style={stickerTextStyle(0.14)}>
                                    Required types: {footprintRequirements.map(r => r.toUpperCase().replace('_', ' ')).join(', ')}
                                </p>
                            )}
                        </div>
                    )}
                    <textarea
                        ref={textareaRef}
                        autoFocus={autoFocus ? true : undefined}
                        rows={3}
                        className={`w-full rounded-3xl p-3 text-xl focus:outline-none resize-none transition-all duration-300 custom-post-creator-textarea ${text.length === 0 ? 'bg-white/10 shadow-inner' : 'bg-transparent'} text-left`}
                        style={{
                            fontFamily: selectedFont?.family || 'inherit',
                            color: selectedTextColor,
                            caretColor: selectedTextColor,
                            maxHeight: '9em', // Roughly 6 lines
                            ...stickerTextStyle(0.18)
                        }}
                        placeholder={isFootprintPost ? "Let's see it.. 👀" : "No pressure, unlike some other apps.."}
                        value={text}
                        onChange={(e) => {
                            const val = e.target.value;
                            setText(val);
                            updateSearch(val, e.target.selectionStart);
                            e.target.style.height = 'auto';
                            e.target.style.height = e.target.scrollHeight + 'px';
                        }}
                        onPaste={handlePaste}
                    />
                    {mentionQuery !== null && (
                        <div className="relative">
                            <MentionDropdown
                                query={mentionQuery}
                                following={usersData[currentUser || '']?.following || []}
                                usersData={usersData}
                                onSelect={(username) => {
                                    const { newText, newCursorPos } = completeMention(text, username, text.length);
                                    setText(newText);
                                    setTimeout(() => {
                                        const el = document.querySelector('.custom-post-creator-textarea') as HTMLTextAreaElement;
                                        if (el) {
                                            el.focus();
                                            el.setSelectionRange(newCursorPos, newCursorPos);
                                        }
                                    }, 10);
                                }}
                                position="below"
                            />
                        </div>
                    )}
                    {attachments.length > 0 && !(attachments[0].type === 'audio' || attachments[0].type === 'voice') && (
                        <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                            {attachments.map(att => {
                                return (
                                    <div key={att.url} className="">
                                        <div className="relative group aspect-square">
                                            {att.type === 'image' && <img src={att.url} className="rounded-lg object-cover w-full h-full" />}
                                            {att.type === 'video' && <video src={att.url.startsWith('blob:') ? att.url : `${att.url}#t=0.001`} className="rounded-lg object-cover w-full h-full" playsInline muted preload="metadata" />}
                                            <button onClick={() => removeAttachment(att.url)} className="absolute -top-1 -right-1 bg-gray-900/80 rounded-full p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                                <CloseIcon className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {attachments.length > 0 && (attachments[0].type === 'audio' || attachments[0].type === 'voice') && (
                        <div className="mt-2 w-full">
                            {attachments.map(att => {
                                return (
                                    <div key={att.url} className="relative group">
                                        <div className="rounded-xl flex items-center justify-center w-full transition-colors group/audio" title="Click to play">
                                            <CustomAudioPlayer src={att.url} name={att.file?.name} forcedDuration={att.duration} />
                                        </div>
                                        <button onClick={() => removeAttachment(att.url)} className="absolute -top-1 -right-1 bg-gray-900/80 rounded-full p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                            <CloseIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {isAudioPost && (
                        <div className="mt-4 p-3 bg-gray-800/50 rounded-lg flex flex-col gap-2">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400" style={stickerTextStyle(0.18)}>
                                {coverImage ? 'Cover Image' : 'Add Cover Image'}
                            </label>
                            <div className="flex items-center gap-3">
                                <input type="file" ref={coverImageInputRef} onChange={handleCoverImageChange} accept="image/*" hidden />
                                <button 
                                    onClick={() => coverImageInputRef.current?.click()} 
                                    className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors flex-shrink-0"
                                    title="Choose cover image"
                                >
                                    <ImageIcon className="w-5 h-5" />
                                </button>
                                
                                <button
                                    onClick={() => {
                                        setTenorSearchMode('cover');
                                        setShowTenorModal(true);
                                    }}
                                    className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-widest rounded-full transition-colors flex-shrink-0"
                                    style={stickerTextStyle(0.18)}
                                    title="Search GIF for cover"
                                >
                                    gif
                                </button>

                                {coverImage && (
                                    <div className="relative group">
                                        <img src={coverImage.url} className="w-12 h-12 rounded-lg object-cover border border-white/10" />
                                        <button onClick={() => setCoverImage(null)} className="absolute -top-1 -right-1 bg-gray-900/80 rounded-full p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                            <CloseIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {isFetchingPreview && <div className="text-center text-gray-400 p-4">Fetching link preview...</div>}

                    {linkPreview && (
                        <div className="mt-4 border border-gray-700 rounded-xl overflow-hidden bg-gray-800/50 relative group">
                            {linkPreview.image ? (
                                <img src={linkPreview.image} alt="Link preview" className="w-full h-32 object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement?.classList.add('no-image'); }} />
                            ) : (
                                <div className="w-full h-32 bg-gray-900 flex items-center justify-center">
                                    <ExternalLinkIcon className="w-12 h-12 text-gray-700" />
                                </div>
                            )}
                            <div className="p-3">
                                <h4 className="font-bold truncate">{linkPreview.title}</h4>
                                <p className="text-sm text-gray-300 line-clamp-2">{linkPreview.description}</p>
                                <p className="text-xs text-gray-500 mt-1">{linkPreview.domain}</p>
                            </div>
                            <button onClick={() => setLinkPreview(null)} className="absolute top-1 right-1 bg-gray-900/80 rounded-full p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                                <CloseIcon className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    {showRecorder && (
                        <AudioRecorder
                            ref={audioRecorderRef}
                            onRecordingComplete={handleRecorderComplete}
                            onReset={handleRecorderReset}
                            autoStart={true}
                            showControls={false}
                            stream={audioStream}
                        />
                    )}

                    {usersData[currentUser || '']?.isVendor && (
                        <div className="mt-3 p-3 bg-black/20 border border-white/10 rounded-2xl flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-xl">🏪</span>
                                    <div className="flex flex-col">
                                        <span className="text-xs font-black uppercase tracking-wider" style={{ color: selectedThemeColor, ...stickerTextStyle(0.15) }}>
                                            List For Sale
                                        </span>
                                        <span className="text-[10px] opacity-60">Offer this post's items on the "For Sale" tab</span>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsForSale(!isForSale)}
                                    className={`w-12 h-6 rounded-full p-1 transition-colors duration-300 ${isForSale ? 'bg-green-500' : 'bg-white/10'}`}
                                >
                                    <div className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform duration-300 ${isForSale ? 'translate-x-6' : 'translate-x-0'}`} />
                                </button>
                            </div>

                            {isForSale && (
                                <div className="flex items-center gap-2 bg-black/30 border border-white/5 rounded-xl p-2.5">
                                    <span className="text-sm font-black opacity-80" style={{ color: selectedThemeColor, ...stickerTextStyle(0.15) }}>$</span>
                                    <input
                                        type="number"
                                        placeholder="0.00"
                                        step="0.01"
                                        min="0"
                                        value={price}
                                        onChange={(e) => setPrice(e.target.value)}
                                        className="bg-transparent text-white placeholder-white/30 text-sm font-bold w-full focus:outline-none"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-2 pt-3 w-full min-w-0">
                        <div
                            className="flex-1 flex items-center overflow-x-auto no-scrollbar scroll-smooth px-2 min-w-0"
                            onTouchStart={(e) => e.stopPropagation()}
                            onTouchMove={(e) => e.stopPropagation()}
                            onTouchEnd={(e) => e.stopPropagation()}
                        >
                            <input type="file" ref={mediaInputRef} onChange={(e) => handleFileChange(e)} accept="image/*,video/*" multiple hidden />
                            {(() => {
                                const isAndroid = Capacitor.getPlatform() === 'android' || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));
                                if (isAndroid) {
                                    return (
                                        <>
                                            <button onClick={() => { setCameraMode('image'); setShowCamera(true); }} className="p-3 hover:bg-white/10 rounded-full transition-all flex-shrink-0" style={{ color: selectedTextColor }} aria-label="Take Photo"><CameraIcon className="w-6 h-6" /></button>
                                            <button onClick={() => { setCameraMode('video'); setShowCamera(true); }} className="p-3 hover:bg-white/10 rounded-full transition-all flex-shrink-0" style={{ color: selectedTextColor }} aria-label="Record Video"><VideoIcon className="w-6 h-6" /></button>
                                        </>
                                    );
                                } else {
                                    return (
                                        <button onClick={() => { setCameraMode('both'); setShowCamera(true); }} className="p-3 hover:bg-white/10 rounded-full transition-all flex-shrink-0" style={{ color: selectedTextColor }} aria-label="Take Photo/Video"><CameraIcon className="w-6 h-6" /></button>
                                    );
                                }
                            })()}
                            <button onClick={() => handleGalleryClick()} className="p-3 hover:bg-white/10 rounded-full transition-all flex-shrink-0" style={{ color: selectedTextColor }} aria-label="Upload Media"><GalleryIcon className="w-6 h-6" /></button>

                            <input type="file" ref={audioInputRef} onChange={(e) => handleFileChange(e, 'audio')} multiple hidden />
                            <button onClick={() => setShowAudioMenuModal(true)} className="p-3 hover:bg-white/10 rounded-full transition-all flex-shrink-0" style={{ color: selectedTextColor }} aria-label="Add Audio Files"><AudioIcon className="w-6 h-6" /></button>
                            <button
                                onClick={async () => {
                                    if (!showRecorder) {
                                        try {
                                            await requestMicrophonePermission();
                                            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                                            setAudioStream(stream);
                                            // Starting a recording: Prune attachments
                                            const existingImages = attachments.filter(a => a.type === 'image');
                                            if (existingImages.length > 0) {
                                                setAttachments([existingImages[0]]);
                                            } else {
                                                setAttachments([]);
                                            }
                                            setShowRecorder(true);
                                        } catch (err) {
                                            console.error("Microphone access failed:", err);
                                        }
                                    } else {
                                        // Stopping a recording - wait for the stop event to trigger handleRecorderComplete
                                        audioRecorderRef.current?.stopRecording();
                                    }
                                }}
                                className={`p-3 rounded-full transition-all flex-shrink-0 ${showRecorder ? 'text-red-500 bg-red-500/10' : 'hover:bg-white/10'}`}
                                style={!showRecorder ? { color: selectedTextColor } : {}}
                                aria-label="Record Voice"
                            >
                                <MicIcon className="w-6 h-6" />
                            </button>

                            <button
                                onClick={() => {
                                    setTenorSearchMode('attachment');
                                    setShowTenorModal(true);
                                }}
                                className="px-3 py-2 hover:bg-white/10 rounded-full transition-all flex-shrink-0 text-lg font-black uppercase tracking-widest"
                                style={{
                                    color: selectedTextColor,
                                    ...stickerTextStyle(0.18),
                                    filter: `drop-shadow(0 0 8px ${selectedThemeColor})`
                                }}
                                aria-label="Add GIF"
                            >
                                gif
                            </button>

                            <button
                                onClick={() => setShowCustomizationModal(true)}
                                className={`p-3 rounded-full transition-all flex-shrink-0 ${showCustomizationModal ? 'text-blue-500 bg-blue-500/10' : 'hover:bg-white/10'}`}
                                style={!showCustomizationModal ? { color: selectedTextColor } : {}}
                                aria-label="Customize Post"
                            >
                                <SettingsIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                            {onCancel && (
                                <button onClick={onCancel} className="text-xs transition-colors px-1 whitespace-nowrap opacity-60 hover:opacity-100" style={{ color: selectedTextColor, ...stickerTextStyle(0.18) }}>
                                    Cancel
                                </button>
                            )}
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.dispatchEvent(new CustomEvent('app.openLinkModal'));
                                }}
                                className="p-1 hover:bg-white/10 rounded-full transition-all active:scale-90 flex-shrink-0 flex items-center justify-center text-3xl"
                                style={{
                                    ...stickerTextStyle(0.18),
                                    filter: `drop-shadow(0 0 8px ${selectedThemeColor})`
                                }}
                                title="Link? Ask your friends to meet up"
                            >
                                📍
                            </button>
                            <button
                                onClick={handlePost}
                                disabled={isSubmitDisabled}
                                className="p-2 hover:bg-white/10 rounded-full transition-all active:scale-90 disabled:opacity-50 flex-shrink-0 flex items-center justify-center shadow-lg"
                                style={{
                                    color: selectedTextColor,
                                    ...stickerTextStyle(0.18)
                                }}
                            >
                                {totalAttachmentSize > MAX_POST_SIZE ? (
                                    <span className="text-[10px] font-bold px-1 uppercase">Too Large</span>
                                ) : (
                                    <SendIcon className="w-8 h-8" />
                                )}
                            </button>
                        </div>
                    </div>

                </div>
            </div>
            {/* Customize Post Modal - Rendered outside scaled div for correct fixed positioning */}
            <PostOptionsModal
                isOpen={showCustomizationModal}
                onClose={() => setShowCustomizationModal(false)}
                isCommunityPost={isCommunityPost}
                isFootprintPost={isFootprintPost}
                showTextEditing={false}
                hideShapeOptions={!attachments.length && !linkPreview}
                postcardShape={postcardShape}
                onConfirm={(data: any) => {
                    if (data.visibility) {
                        setVisibility(data.visibility);
                        localStorage.setItem(`post_creator_visibility_${currentUser}`, JSON.stringify(data.visibility));
                    }
                    if (data.visibilityGroups !== undefined) {
                        setVisibilityGroups(data.visibilityGroups);
                        localStorage.setItem(`post_creator_visibilityGroups_${currentUser}`, JSON.stringify(data.visibilityGroups));
                    }
                    if (data.showCaption !== undefined) setShowCaption(data.showCaption);
                    if (data.showOnProfile !== undefined) setShowOnProfile(data.showOnProfile);
                    if (data.fontId) setSelectedFontId(data.fontId);
                    if (data.postcardStyle) setSelectedPostcardStyle(data.postcardStyle);
                    if (data.themeColor) setSelectedThemeColor(data.themeColor);
                    if (data.themeOpacity !== undefined) setSelectedThemeOpacity(data.themeOpacity);
                    if (data.textColor) setSelectedTextColor(data.textColor);
                    if (data.textSize) setTextSize(data.textSize);
                    if (data.postcardShape) setPostcardShape(data.postcardShape);
                }}
                post={useMemo(() => ({
                    visibility,
                    visibilityGroups,
                    showCaption,
                    showOnProfile,
                    fontId: selectedFontId,
                    postcardStyle: selectedPostcardStyle,
                    themeColor: selectedThemeColor,
                    themeOpacity: selectedThemeOpacity,
                    textColor: selectedTextColor,
                    textSize,
                    postcardShape,
                    type: attachments.length ? (attachments[0].type as any) : (linkPreview ? 'link' : 'text'),
                    content: { 
                        text: text || "",
                        media: attachments.map(a => ({ 
                            url: a.url, 
                            type: a.type, 
                            width: a.width, 
                            height: a.height, 
                            isLive: a.isLive,
                            originalUrl: a.originalUrl,
                            name: a.name || a.file?.name
                        })),
                        linkPreview: linkPreview || undefined
                    },
                    author: currentUser || '',
                    id: 0,
                    likes: 0,
                    comments: [] as Comment[],
                    bumps: 0,
                    bookmarks: 0,
                    shares: 0,
                    createdAt: new Date().toISOString()
                }), [
                    visibility,
                    visibilityGroups,
                    showCaption,
                    showOnProfile,
                    selectedFontId,
                    selectedPostcardStyle,
                    selectedThemeColor,
                    selectedThemeOpacity,
                    selectedTextColor,
                    textSize,
                    postcardShape,
                    text,
                    currentUser
                ])}
            />

            <NativeCamera
                isOpen={showCamera}
                onClose={() => setShowCamera(false)}
                onCapture={handleCameraCapture}
                onOpenGallery={() => {
                    setShowCamera(false);
                    mediaInputRef.current?.click();
                }}
                captureMode={cameraMode}
            />

            {showTenorModal && (() => {
                const userThemeColor = usersData[currentUser || '']?.appThemeColor || '#3b82f6';
                const userTextColor = usersData[currentUser || '']?.appTextColor || '#ffffff';
                const userOpacity = (usersData[currentUser || '']?.postBlurOpacity !== undefined ? usersData[currentUser || '']?.postBlurOpacity : 50) / 100;
                const modalBg = getExactModalBg(userThemeColor, userOpacity);
                return (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center z-[150] p-4 animate-fade-in" onClick={() => setShowTenorModal(false)}>
                        <div className="bg-[#0e0e11] border border-white/10 rounded-[2.5rem] w-full max-w-md h-[80vh] overflow-hidden flex flex-col shadow-2xl backdrop-blur-md" style={{ backgroundColor: modalBg, color: userTextColor }} onClick={e => e.stopPropagation()}>

                            {/* Header */}
                            <div className="p-5 flex items-center justify-between border-b border-white/5">
                                <div className="flex items-center gap-2">
                                    <SparklesIcon className="w-5 h-5 animate-pulse" style={{ color: userThemeColor }} />
                                    <h3 className="text-sm font-black uppercase tracking-tight" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                                        Search Tenor GIFs
                                    </h3>
                                </div>
                                <button
                                    onClick={() => setShowTenorModal(false)}
                                    className="p-1.5 hover:bg-white/10 rounded-full"
                                    style={{ color: userTextColor }}
                                >
                                    <CloseIcon className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Search input */}
                            <div className="p-4 border-b border-white/5">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search premium GIFs..."
                                        value={tenorSearch}
                                        onChange={(e) => handleTenorSearch(e.target.value)}
                                        className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-2xl py-3 pl-10 pr-4 text-xs outline-none"
                                        style={{ color: userTextColor }}
                                    />
                                    <SearchIcon className="w-4 h-4 text-white/40 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: userThemeColor }} />
                                </div>
                            </div>

                            {/* Results */}
                            <div className="flex-1 overflow-y-auto p-4 allow-scroll">
                                {loadingTenor ? (
                                    <div className="flex flex-col items-center justify-center h-full py-12">
                                        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mb-3" style={{ borderColor: userThemeColor, borderTopColor: 'transparent' }} />
                                        <span className="text-[10px] font-black uppercase tracking-wider" style={{ color: userThemeColor, ...stickerTextStyle(0.18) }}>Searching Tenor...</span>
                                    </div>
                                ) : tenorResults.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full py-12 text-center">
                                        <span className="text-[10px] uppercase tracking-widest opacity-40" style={stickerTextStyle(0.18)}>{tenorSearch.trim() ? "No results found" : "Type to start searching"}</span>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 gap-2">
                                        {tenorResults.map((r) => (
                                            <div
                                                key={r.id}
                                                onClick={() => handleAttachTenorGif(r.url)}
                                                className="aspect-video bg-black/40 rounded-xl overflow-hidden cursor-pointer border border-white/5 transition-all transform active:scale-95"
                                                style={{ borderColor: 'rgba(255,255,255,0.05)' }}
                                            >
                                                <img src={r.url} className="w-full h-full object-cover" alt={r.name} loading="lazy" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                );
            })()}

            {showAudioMenuModal && (() => {
                const userThemeColor = usersData[currentUser || '']?.appThemeColor || '#3b82f6';
                const userTextColor = usersData[currentUser || '']?.appTextColor || '#ffffff';
                const userOpacity = (usersData[currentUser || '']?.postBlurOpacity !== undefined ? usersData[currentUser || '']?.postBlurOpacity : 50) / 100;
                const modalBg = getExactModalBg(userThemeColor, userOpacity);
                return (
                    <div className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center z-[150] p-4 animate-fade-in" onClick={() => setShowAudioMenuModal(false)}>
                        <div className="bg-[#0e0e11] border border-white/10 rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col shadow-2xl backdrop-blur-md" style={{ backgroundColor: modalBg, color: userTextColor }} onClick={e => e.stopPropagation()}>
                            
                            {/* Header */}
                            <div className="p-5 flex items-center justify-between border-b border-white/5">
                                <div className="flex items-center gap-2">
                                    <AudioIcon className="w-5 h-5" style={{ color: userThemeColor }} />
                                    <h3 className="text-sm font-black uppercase tracking-tight" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                                        Upload Audio / Track Preview
                                    </h3>
                                </div>
                                <button
                                    onClick={() => setShowAudioMenuModal(false)}
                                    className="p-1.5 hover:bg-white/10 rounded-full"
                                    style={{ color: userTextColor }}
                                >
                                    <CloseIcon className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="p-6 flex flex-col gap-4 overflow-y-auto max-h-[70vh]">
                                
                                {/* Upload Track option */}
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-[9px] font-bold uppercase ml-1" style={stickerTextStyle(0.18)}>Upload audio track</label>
                                    <button
                                        onClick={() => audioInputRef.current?.click()}
                                        disabled={isUploading}
                                        className="w-full bg-black/30 border border-white/10 hover:border-white/30 focus:border-white/30 rounded-2xl py-3 px-4 text-xs font-bold text-center transition-colors flex items-center justify-center gap-2"
                                        style={{ color: userTextColor }}
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        {isUploading ? `Uploading (${uploadProgress}%)` : 'Upload MP3 / Audio File'}
                                    </button>
                                    <input
                                        type="file"
                                        ref={audioInputRef}
                                        onChange={handleFileUploadForPost}
                                        className="hidden"
                                        accept="audio/*"
                                    />
                                </div>

                                {/* Streaming Services Toggles */}
                                <div className="flex flex-col gap-1.5 border-t border-white/10 pt-4">
                                    <label className="text-[9px] font-bold uppercase ml-1" style={stickerTextStyle(0.18)}>Add Track from streaming link</label>
                                    
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setActiveInputService(activeInputService === 'spotify' ? null : 'spotify');
                                                setExternalError('');
                                            }}
                                            className="bg-black/30 border border-white/10 hover:border-white/30 rounded-2xl py-2 px-1 text-[10px] font-black uppercase text-center transition-colors flex items-center justify-center gap-1"
                                            style={{ 
                                                color: '#1DB954', 
                                                borderColor: activeInputService === 'spotify' ? '#1DB954' : 'rgba(255,255,255,0.1)',
                                                ...stickerTextStyle(0.18) 
                                            }}
                                        >
                                            🎵 Spotify
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setActiveInputService(activeInputService === 'apple' ? null : 'apple');
                                                setExternalError('');
                                            }}
                                            className="bg-black/30 border border-white/10 hover:border-white/30 rounded-2xl py-2 px-1 text-[10px] font-black uppercase text-center transition-colors flex items-center justify-center gap-1"
                                            style={{ 
                                                color: '#fc3c44', 
                                                borderColor: activeInputService === 'apple' ? '#fc3c44' : 'rgba(255,255,255,0.1)',
                                                ...stickerTextStyle(0.18) 
                                            }}
                                        >
                                            🍎 Apple
                                        </button>
                                    </div>

                                    {/* Dynamic Streaming Form */}
                                    {activeInputService && (
                                        <form 
                                            onSubmit={handleAddStreamingAudio}
                                            className="bg-black/20 border border-white/5 rounded-2xl p-4 mt-2 flex flex-col gap-3 animate-fade-in"
                                        >
                                            <div className="flex items-center justify-between">
                                                <span 
                                                    className="text-[9px] font-black uppercase tracking-wider"
                                                    style={{ 
                                                        color: activeInputService === 'spotify' ? '#1DB954' : activeInputService === 'apple' ? '#fc3c44' : '#ff0000', 
                                                        ...stickerTextStyle(0.18) 
                                                    }}
                                                >
                                                    Add {activeInputService === 'spotify' ? 'Spotify Track' : activeInputService === 'apple' ? 'Apple Music Track' : 'YouTube Music Track'}
                                                </span>
                                                <button 
                                                    type="button"
                                                    onClick={() => {
                                                        setActiveInputService(null);
                                                        setExternalError('');
                                                    }}
                                                    className="text-[8px] font-black uppercase text-white/40 hover:text-white"
                                                >
                                                    Cancel
                                                </button>
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <label className="text-[8px] font-bold uppercase text-white/50 px-1">Song Link / URL</label>
                                                <input 
                                                    type="text"
                                                    placeholder={
                                                        activeInputService === 'spotify' 
                                                            ? "https://open.spotify.com/track/..." 
                                                            : activeInputService === 'apple' 
                                                                ? "https://music.apple.com/..." 
                                                                : "https://music.youtube.com/watch?v=..."
                                                    }
                                                    value={externalLink}
                                                    onChange={(e) => setExternalLink(e.target.value)}
                                                    className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-2 px-3 text-xs outline-none text-white font-mono"
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <label className="text-[8px] font-bold uppercase text-white/50 px-1">Song Title (Optional)</label>
                                                <input 
                                                    type="text"
                                                    placeholder="Enter song name and artist..."
                                                    value={externalTitle}
                                                    onChange={(e) => setExternalTitle(e.target.value)}
                                                    className="w-full bg-black/30 border border-white/10 focus:border-white/30 rounded-xl py-2 px-3 text-xs outline-none text-white"
                                                />
                                            </div>

                                            {externalError && (
                                                <p className="text-[9px] font-black uppercase text-red-400 px-1">{externalError}</p>
                                            )}

                                            <p className="text-[9px] text-white/30 px-1 leading-relaxed">
                                                not the song you're looking for? you can just post the link but it won't look as nice. sry :/
                                            </p>

                                            <button
                                                type="submit"
                                                className="w-full py-2 px-4 rounded-xl text-[10px] font-black uppercase tracking-wider text-white transition-all text-center cursor-pointer"
                                                style={{ 
                                                    backgroundColor: activeInputService === 'spotify' ? '#1DB954' : '#fc3c44',
                                                    ...stickerTextStyle(0.18)
                                                }}
                                            >
                                                Attach Track
                                            </button>
                                        </form>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </>
    );
};
export const FootprintPostCreatorModal = ({ isOpen, onClose, achievement, onSubmit, onInternalModalStateChange, closeInternalModalsTrigger = 0, isTaskOfTheDay = false, onUploadFailed, footprintRequirements = [] }: {
    isOpen: boolean, onClose: () => void, achievement: Achievement | null, onSubmit: (postData: {
        type: PostType,
        content: Post["content"],
        visibility?: "public" | "followers" | "groups" | "private",
        visibilityGroups?: string[],
        showCaption?: boolean,
        showOnProfile?: boolean,
        textSize?: 'xs' | 'sm' | 'med' | 'lg',
        communityId?: string,
        themeOpacity?: number,
        isOptimistic?: boolean,
        localId?: number,
        fontId?: string,
        postcardStyle?: string,
        postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall'
    }) => void, onInternalModalStateChange?: (isOpen: boolean) => void, closeInternalModalsTrigger?: number, isTaskOfTheDay?: boolean, onUploadFailed?: (localId: number) => void, footprintRequirements?: string[]
}) => {
    useEffect(() => {
        if (isOpen && closeInternalModalsTrigger > 0) {
            onClose();
        }
    }, [closeInternalModalsTrigger, isOpen, onClose]);

    if (!isOpen || !achievement) return null;

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80]"
            onClick={onClose}
        >
            <div className="rounded-2xl shadow-lg p-4 max-w-xl w-full mx-4 backdrop-blur-md" style={{ backgroundColor: 'var(--app-theme-bg)' }} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-2 px-2">
                    <h2 className="text-xl font-bold">Complete Achievement</h2>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-gray-400 transition-colors">
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </div>
                <PostCreator
                    onCreatePost={onSubmit}
                    postBlurOpacity={80} // Dummy value, overridden by modal styles
                    isFootprintPost={true}
                    achievement={achievement}
                    isTaskOfTheDay={isTaskOfTheDay}
                    onCancel={onClose}
                    onInternalModalStateChange={onInternalModalStateChange}
                    closeInternalModalsTrigger={closeInternalModalsTrigger}
                    autoFocus={true}
                    onUploadFailed={onUploadFailed}
                    footprintRequirements={footprintRequirements}
                />
            </div>
        </div>
    );
};
